import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import {
  aggregateWindowProvider,
  IAggregates,
} from '../../../../data/providers/aggregatesProvider';
import {
  IMetricHistorySetting,
  ISessionWindowOption,
  metricHistorySettingProvider,
  sessionLimitOptions,
} from '../../../../data/providers/settings/metricHistorySettingProvider';
import {
  IAthlete,
  IComputedAggregate,
  ISelectedLabelKey,
  ITest,
} from '../../../../data/types';
import { setLimitSessionSetting } from '../../../../data/writers/settings';
import { saveMetricHistorySetting } from '../../../../data/writers/settings/metricHistorySetting';
import { Card } from '../../../shared/card/Card';
import { CardHeader } from '../../../shared/card/CardHeader';
import { Select } from '../../../shared/form/Select';
import { GraphContainer } from '../../../shared/graphs/GraphContainer';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { MetricGraph } from './MetricGraph';

interface IProps extends RouteComponentProps {
  test: ITest;
  athlete: IAthlete;
  metric: ISelectedLabelKey;
}

const MetricHistoryComponent = (props: IProps): React.ReactElement => {
  const [settingsLoading, setSettingsLoading] = React.useState<boolean>(true);
  const [dataLoading, setDataLoading] = React.useState<boolean>(true);
  const [sessionLimits, setSessionLimits] =
    React.useState<ISessionWindowOption>();

  const [sessions, setSessions] = React.useState<IComputedAggregate[]>([]);

  /**
   * @description handles change to session limit
   * @param setting
   */
  const sessionLimitChangeHandler = (setting: ISessionWindowOption): void => {
    saveMetricHistorySetting({ sessionLimits: setting });
    setLimitSessionSetting(setting);
  };

  /**
   * @description determines if the supplied test has a valid value for the selected metric
   * @param metricKey {string}
   * @param test {ITest}
   * @returns {boolean}
   */
  const testContainsMetric = (metricKey: string, test: ITest): boolean => {
    // make sure the test contains the metric AND the metric is an active metric
    return test[metricKey] && test[metricKey].value;
  };

  React.useEffect((): (() => void) => {
    if (!sessionLimits?.value) return;
    setDataLoading(true);

    const subscriber = aggregateWindowProvider(
      props.test.athleteId,
      'athletes',
      props.test.queryHash,
      sessionLimits.value,
      props.test.sessionId
    ).subscribe((mData: IAggregates): void => {
      setDataLoading(false);
      setSessions(mData.sessions);
    });
    return (): void => {
      subscriber.unsubscribe();
    };
  }, [props.test.id, sessionLimits?.value]);

  /**
   * @description subscribes to the sessions limit setting
   */
  React.useEffect((): (() => void) => {
    const subscriber = metricHistorySettingProvider().subscribe(
      (setting: IMetricHistorySetting): void => {
        setSettingsLoading(false);
        setSessionLimits(setting.sessionLimits);
      }
    );
    return (): void => {
      subscriber.unsubscribe();
    };
  }, []);

  return (
    <Card
      style={{
        minHeight: 'var(--graph-min-height)',
        padding: '0px',
      }}
    >
      <HistoryContainer>
        {(!!props.metric && !!props.test) || settingsLoading ? (
          <React.Fragment>
            <CardHeader style={{ alignItems: 'center' }}>
              <div style={{ flex: 2, display: 'flex' }}>
                <h6>{props.metric.label}</h6>
              </div>
              <div style={{ flex: 1 }}>
                <Select
                  options={sessionLimitOptions}
                  value={sessionLimits}
                  onChange={sessionLimitChangeHandler}
                  placeholder='Select Sessions...'
                />
              </div>
            </CardHeader>

            <GraphContainer
              style={{ paddingTop: '0px', height: 'calc(100% - 60px)' }}
            >
              {dataLoading ? (
                <LinearLoading />
              ) : (
                <MetricGraph
                  test={props.test}
                  selectedMetric={props.metric}
                  sessions={sessions}
                />
              )}
            </GraphContainer>
          </React.Fragment>
        ) : (
          <LinearLoading />
        )}
      </HistoryContainer>
    </Card>
  );
};

export const MetricHistory = withRouter(MetricHistoryComponent);

const HistoryContainer = styled.div`
  width: 100%;
  height: 100%;
`;
